import { extractClassGroupNamesFromEvent } from '@bp/shared-planung-utils';
import {
  Button,
  Grid,
  GridColumn,
  GridRow,
  Pagination,
  showToast,
  Table,
  TableColumns,
  Tooltip,
  useDefaultSelecting,
} from '@bp/ui-components';
import { SortingState } from '@tanstack/react-table';
import dayjs from 'dayjs';
import { observer } from 'mobx-react-lite';
import { useEffect, useMemo, useState } from 'react';
import { useAuthClaims } from '../../hooks/useAuthClaims';
import { useConfirm } from '../../hooks/useConfirm';
import { useLoadBasicData } from '../../hooks/useLoadBasicData';
import { usePermissionChecker } from '../../hooks/usePermissionChecker';
import { useSelectedSchoolYear } from '../../hooks/useSelectedSchoolYear';
import {
  EventJobStatus,
  use_AdminEventsCountQuery,
  use_AdminEventsQuery,
  useCancelEventJobMutation,
  useEventJobsQuery,
} from '../../types/planung-graphql-client-defs';
import { createEvents, deleteEvents } from '../../utils/eventsApi';
import { toSharedGroupsData } from '../../utils/sharedGroupsData';
import styles from './AdminEvents.module.scss';
import { AdminSchoolEventLessonSyncs } from './AdminSchoolEventLessonSyncs';

type EventJobTableType = {
  uuid: string;
  from: string;
  to: string;
  processed: number;
  createdEvents: number;
  updatedEvents: number;
  deletedEvents: number;
  status: EventJobStatus;
  created: string;
};

const formatDateTime = (value?: string | null) =>
  value && dayjs(value).isValid() ? dayjs(value).format('DD.MM.YYYY HH:mm') : '–';

const statusClass: Record<EventJobStatus, string> = {
  [EventJobStatus.Finished]: styles.success,
  [EventJobStatus.Running]: styles.running,
  [EventJobStatus.Cancelled]: styles.warning,
  [EventJobStatus.Error]: styles.error,
};

type AdminEventsTableType = {
  uuid: string;
  start: string;
  end: string;
  comment: string;
  rooms: string;
  teachers: string;
  classesOrGroups: string;
  originalClassesOrGroups: string;
  subject: string;
};

export const AdminEvents = observer(() => {
  const { pimAuthClaims } = useAuthClaims();
  const perms = usePermissionChecker();

  const pageSize = 15;
  const [eventPage, setEventPage] = useState(0);

  const schoolYear = useSelectedSchoolYear();

  const [{ data: _eventJobsData }, execute] = useEventJobsQuery({
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });
  const [, cancelEventJob] = useCancelEventJobMutation();

  const hasRunningEventJobs = _eventJobsData?.eventJobs.some((job) => job.status === EventJobStatus.Running);

  async function dangerHandleStopSync(uuid: string) {
    if (perms?.isOmniAdmin()) {
      await cancelEventJob({ uuid });
    }
    execute();
  }

  const [{ data }, fetch] = use_AdminEventsQuery({
    variables: {
      start: schoolYear?.start ?? '',
      end: schoolYear?.end ?? '',
      limit: pageSize + 1,
      offset: eventPage * pageSize,
    },
    pause: !schoolYear,
    requestPolicy: 'network-only',
  });

  const [{ data: countData }] = use_AdminEventsCountQuery({
    variables: { start: schoolYear?.start ?? '', end: schoolYear?.end ?? '' },
    pause: !data,
    requestPolicy: 'network-only',
  });

  useEffect(() => {
    if (hasRunningEventJobs) {
      const intervalId = setInterval(() => {
        execute();
      }, 2000);

      return () => {
        clearInterval(intervalId);
        fetch();
      };
    }
  }, [execute, fetch, hasRunningEventJobs]);

  const eventTotalCount = countData?.adminEventsCount ?? 0;
  const pageCount = Math.ceil(eventTotalCount / pageSize);

  const { ConfirmationDialog, confirm } = useConfirm();

  const handleCreateEvents = async () => {
    await confirm({
      title: 'Create Events',
      message: 'Do you really want to create events?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: async () => {
        if (schoolYear) {
          await createEvents({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: schoolYear?.uuid,
          });
          showToast('Events creation started. Please wait up to 20 minutes for the changes to take effect.', {
            autoClose: 1200000,
            type: 'success',
            closeButton: false,
            onClose: () => {
              fetch();
            },
          });
        }
        const intervalId = setInterval(() => {
          execute();
        }, 1000);

        setTimeout(() => {
          clearInterval(intervalId);
        }, 1200000);
      },
    });
  };

  const handleDeleteEvents = async () => {
    await confirm({
      title: 'Delete Events',
      message: 'Do you really want to delete all events?',
      confirmText: 'Yes',
      cancelText: 'No',
      onConfirm: async () => {
        if (schoolYear) {
          await deleteEvents({
            organizationUuid: pimAuthClaims.getOrganizationUuid(),
            schoolYearUuid: schoolYear?.uuid,
          });
          showToast('Events deletion started. Please wait up to 3 seconds for the changes to take effect.', {
            autoClose: 3000,
            type: 'success',
            onClose: () => {
              fetch();
            },
          });
        }
      },
    });
  };

  const { groupsData, teacherData, subjectData, classesData, roomsData, divisionsData } = useLoadBasicData({
    pause: !schoolYear,
  });
  const sharedGroupsData = useMemo(() => toSharedGroupsData(groupsData), [groupsData]);

  const eventJobColumns: TableColumns<EventJobTableType>[] = [
    {
      id: 'status',
      accessorKey: 'status',
      header: 'Status',
      size: 120,
      cell: ({ row }) => (
        <span className={`${styles.status} ${statusClass[row.original.status] ?? ''}`}>{row.original.status}</span>
      ),
    },
    {
      id: 'created',
      accessorKey: 'created',
      header: 'Created',
      size: 150,
    },
    {
      id: 'range',
      header: 'Period',
      size: 300,
      accessorFn: ({ from, to }) => `${from} – ${to}`,
    },
    {
      id: 'processed',
      accessorKey: 'processed',
      header: 'Processed',
      size: 100,
    },
    {
      id: 'changes',
      header: 'Created / Updated / Deleted',
      size: 220,
      cell: ({ row }) => (
        <span className={styles.counts}>
          <span>+{row.original.createdEvents}</span>
          <span>~{row.original.updatedEvents}</span>
          <span>−{row.original.deletedEvents}</span>
        </span>
      ),
    },
    {
      id: 'uuid',
      accessorKey: 'uuid',
      header: 'UUID',
      cell: ({ cell }) => (
        <Tooltip content={cell.getValue() as string}>
          <span className={styles.mono}>{(cell.getValue() as string).slice(0, 8)}</span>
        </Tooltip>
      ),
    },
  ];

  const eventJobData: EventJobTableType[] = useMemo(() => {
    return (
      _eventJobsData?.eventJobs.map((job) => {
        return {
          uuid: job.uuid,
          from: formatDateTime(job.from),
          to: formatDateTime(job.to),
          processed: job.processed,
          createdEvents: job.createdEvents,
          updatedEvents: job.updatedEvents,
          deletedEvents: job.deletedEvents,
          status: job.status,
          created: formatDateTime(job.createdAt),
        };
      }) ?? []
    );
  }, [_eventJobsData?.eventJobs]);

  const columns: TableColumns<AdminEventsTableType>[] = [
    {
      id: 'start',
      accessorFn: ({ start }) => dayjs(start).utc().format('DD.MM.YYYY HH:mm Z'),
      header: 'Start',
      size: 200,
    },
    {
      id: 'end',
      accessorKey: 'end',
      header: 'End',
      size: 200,
      accessorFn: ({ end }) => dayjs(end).utc().format('DD.MM.YYYY HH:mm Z'),
    },
    {
      id: 'subject',
      accessorKey: 'subject',
      header: 'Subject',
    },
    {
      id: 'teachers',
      accessorKey: 'teachers',
      header: 'Teachers',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'classesOrGroups',
      accessorKey: 'classesOrGroups',
      header: 'Classes / Groups',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'originalClassesOrGroups',
      accessorKey: 'originalClassesOrGroups',
      header: 'Original classes / groups',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'rooms',
      accessorKey: 'rooms',
      header: 'Rooms',
      cell: ({ cell }) => {
        const value = cell.getValue() as string;
        return <Tooltip content={value}>{value}</Tooltip>;
      },
    },
    {
      id: 'comment',
      accessorKey: 'comment',
      header: 'Comment',
    },
    {
      id: 'uuid',
      accessorKey: 'uuid',
      header: 'UUID',
      size: 100,
      cell: ({ cell }) => (
        <Tooltip content={cell.getValue() as string}>
          <span className={styles.mono}>{(cell.getValue() as string).slice(0, 8)}</span>
        </Tooltip>
      ),
    },
  ];

  const tableData: AdminEventsTableType[] = useMemo(() => {
    const events: AdminEventsTableType[] =
      data?.adminEvents.map((event) => {
        const subject = subjectData?.subjects.find((s) => s.uuid === event.subjectUuid);
        const teacherNames = event.persons.map(
          (person) => teacherData?.people.find((t) => t.uuid === person.uuid)?.displayName,
        );

        const rooms = event.roomUuids.map((uuid) => roomsData?.rooms.find((r) => r.uuid === uuid));

        const { groupClassNamesLong } = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.classesOrGroups,
          data: { classesData, groupsData: sharedGroupsData, divisionsData },
        });

        const { groupClassNamesLong: originalGroupClassNamesLong } = extractClassGroupNamesFromEvent({
          eventClassesOrGroups: event.originalClassesOrGroups,
          data: { classesData, groupsData: sharedGroupsData, divisionsData },
        });

        return {
          uuid: event.uuid,
          start: event.start,
          end: event.end,
          comment: event.comment ?? '',
          rooms: rooms.map((room) => room?.name).join(', '),
          teachers: teacherNames.join(', '),
          classesOrGroups: groupClassNamesLong,
          originalClassesOrGroups: originalGroupClassNamesLong,
          subject: subject?.name ?? '',
        };
      }) ?? [];

    // group by start
    const groupedByStart = events.reduce(
      (acc, event) => {
        const start = event.start;
        if (!acc[start]) {
          acc[start] = [];
        }
        acc[start].push(event);
        return acc;
      },
      {} as Record<string, AdminEventsTableType[]>,
    );

    // sort by class
    const sortedEvents = Object.values(groupedByStart).map((group) =>
      group.sort((a, b) => {
        const aClass = a.classesOrGroups
          ? a.classesOrGroups
          : a.originalClassesOrGroups
            ? a.originalClassesOrGroups
            : '';
        const bClass = b.classesOrGroups
          ? b.classesOrGroups
          : b.originalClassesOrGroups
            ? b.originalClassesOrGroups
            : '';
        return aClass.localeCompare(bClass, undefined, { numeric: true, sensitivity: 'base' });
      }),
    );

    return sortedEvents.flat();
  }, [
    classesData,
    data?.adminEvents,
    divisionsData,
    sharedGroupsData,
    roomsData?.rooms,
    subjectData?.subjects,
    teacherData?.people,
  ]);

  const [sorting, setSorting] = useState<SortingState>([]);
  const { rowSelection, onRowSelectionChange } = useDefaultSelecting();

  return (
    <Grid>
      <GridRow
        headline={'Event jobs'}
        tooltip={'Synchronisation of lessons, substitutions and school events into events (newest first).'}
      >
        <GridColumn width={12}>
          <Table
            columns={eventJobColumns}
            data={eventJobData}
            showPagination={true}
            pageSize={5}
            emptyStateSettings={{ size: 'small', hideIcon: true, title: 'No event jobs', subtitle: '' }}
            lastColWidth='90px'
            lastCol={({ original }) =>
              perms?.isOmniAdmin() &&
              original.status === EventJobStatus.Running && (
                <Button hierarchy='secondary' onClick={() => dangerHandleStopSync(original.uuid)}>
                  Stop
                </Button>
              )
            }
          />
        </GridColumn>
      </GridRow>
      <AdminSchoolEventLessonSyncs />
      <GridRow headline={'Events'} tooltip={'Events of the selected school year.'}>
        <GridColumn width={12}>
          <Table
            loading={hasRunningEventJobs}
            isOnWhite={false}
            showSort={true}
            showActionBar={true}
            sorting={sorting}
            showSelect={true}
            rowSelection={rowSelection}
            onRowSelectionChange={onRowSelectionChange}
            onSortingChange={setSorting}
            actionBarSettings={{
              showSelectedRowsCount: true,
              extendedActionsRight: (
                <>
                  <Button onClick={handleCreateEvents} hierarchy={'primary'} disabled={hasRunningEventJobs}>
                    Create Events
                  </Button>
                  <Button onClick={handleDeleteEvents} hierarchy={'primary'} disabled={hasRunningEventJobs}>
                    Delete all Events
                  </Button>
                </>
              ),
            }}
            columns={columns}
            data={tableData}
          />
          <Pagination
            pageCount={pageCount}
            currentPage={eventPage}
            nextPage={() => {
              setEventPage(eventPage + 1);
            }}
            previousPage={() => {
              setEventPage(eventPage - 1);
            }}
            lastPage={() => {
              setEventPage(pageCount - 1);
            }}
            firstPage={() => {
              setEventPage(0);
            }}
            setPageIndex={(index) => {
              setEventPage(index);
            }}
            getCanPreviousPage={() => eventPage > 0}
            getCanNextPage={() => eventPage < pageCount - 1}
          />
        </GridColumn>
      </GridRow>
      <ConfirmationDialog />
    </Grid>
  );
});
